import {
  Button,
  Card,
  Combobox,
  DatePicker,
  Flex,
  Heading,
  Select,
  TextInput,
  TextInputProps,
  createDatePickerValidator,
  createForm,
  useToasts,
  Text,
} from '@applyboard/crystal-ui'
import { COUNTRY_NAMES } from '@applyboard/school-backend-lib/dist/common-types/country'
import { AccountCircleOutlineIcon } from '@applyboard/ui-icons'
import { GENDER_NAMES } from '@applyboard/school-backend-lib/dist/common-types/gender'
import { Loading } from '../../components/Loading'
import { NavBars } from '../../components/NavBars'
import {
  useAddProfileEmailChallenge,
  useGetProfileEmailChallenge,
  useGetProfile,
  useLoggedInUser,
  useUpdateProfile,
  useValidateProfileEmail,
  useVerifyProfileEmailChallenge,
} from '../../hooks'
import { convertTimelessDateStrToLocalDate } from '../../utils/convertTimelessDateStrToLocalDate'
import { ComponentType, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ConfirmCloseEmailDialog } from './ConfirmCloseEmailDialog'
import { NewEmailOtpDialog } from './NewEmailOtpDialog'
import { OldEmailOtpDialog } from './OldEmailOtpDialog'
import { UpdateEmailDialog } from './UpdateEmailDialog'

type ProfileFormFields = {
  givenName: string
  familyName: string
  middleName: string
  dateOfBirth: string
  nationality: string
  gender: string
  email: string
}

const { Form, Field, useSetFieldValues } = createForm<ProfileFormFields>()

export function ProfilePage() {
  const toast = useToasts()
  const { username } = useLoggedInUser()
  const { isLoadingProfile, profile } = useGetProfile({ id: username })
  const { isUpdatingProfile, updateProfile } = useUpdateProfile({ id: username })
  const queryClient = useQueryClient()

  const { isValidatingEmail, validateEmail } = useValidateProfileEmail({ id: username })
  const { isCreatingEmailChallenge, createEmailChallenge } = useGetProfileEmailChallenge({
    id: username,
  })
  const { isSubmittingEmailChallenge, submitEmailChallenge } = useAddProfileEmailChallenge({
    id: username,
  })
  const { isVerifyingEmailChallenge, verifyEmailChallenge } = useVerifyProfileEmailChallenge({
    id: username,
  })

  const [isUpdateEmailDialogOpen, setIsUpdateEmailDialogOpen] = useState(false)
  const [isOldEmailDialogOpen, setIsOldEmailDialogOpen] = useState(false)
  const [isNewEmailDialogOpen, setIsNewEmailDialogOpen] = useState(false)
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false)
  const [pendingNewEmail, setPendingNewEmail] = useState('')
  const [updateEmailDialogKey, setUpdateEmailDialogKey] = useState(0)
  const [oldEmailDialogKey, setOldEmailDialogKey] = useState(0)
  const [newEmailDialogKey, setNewEmailDialogKey] = useState(0)
  const [emailChallenge, setEmailChallenge] = useState<{
    challengeId: string
    emailId: string
  } | null>(null)
  const [newEmailChallenge, setNewEmailChallenge] = useState<{
    challengeId: string
    emailId: string
  } | null>(null)

  if (isLoadingProfile) {
    return (
      <NavBars>
        <Loading />
      </NavBars>
    )
  }

  if (!profile) {
    return null
  }

  const isEmailFlowLoading =
    isValidatingEmail ||
    isCreatingEmailChallenge ||
    isSubmittingEmailChallenge ||
    isVerifyingEmailChallenge

  const currentEmail = profile.attributes.email || ''

  const resetEmailFlow = () => {
    setPendingNewEmail('')
    setEmailChallenge(null)
    setNewEmailChallenge(null)
    setIsUpdateEmailDialogOpen(false)
    setIsOldEmailDialogOpen(false)
    setIsNewEmailDialogOpen(false)
    setUpdateEmailDialogKey(key => key + 1)
    setOldEmailDialogKey(key => key + 1)
    setNewEmailDialogKey(key => key + 1)
  }

  return (
    <NavBars>
      <Form
        defaultValues={{
          givenName: profile.attributes.givenName,
          familyName: profile.attributes.familyName || '',
          middleName: profile.attributes.middleName || '',
          dateOfBirth: profile.attributes.dateOfBirth
            ? convertTimelessDateStrToLocalDate(profile.attributes.dateOfBirth).toISOString()
            : '',
          nationality: profile.attributes.nationality || '',
          gender: profile.attributes.gender || '',
          email: profile.attributes.email || '',
        }}
        onSubmit={data => {
          updateProfile(data, {
            onSuccess: () => {
              toast.positive('Changes to your profile successfully saved.')
            },
            onError: err => {
              if (err instanceof Error) {
                toast.negative(err)
              } else {
                toast.negative(new Error('Something went wrong when saving your profile.'))
              }
            },
          })
        }}
      >
        <ProfilePageForm
          loading={isUpdatingProfile}
          email={currentEmail}
          onUpdateEmailClick={() => setIsUpdateEmailDialogOpen(true)}
        />
      </Form>

      <UpdateEmailDialog
        key={`update-email-${updateEmailDialogKey}`}
        open={isUpdateEmailDialogOpen}
        loading={isEmailFlowLoading}
        currentEmail={currentEmail}
        onOpenChange={setIsUpdateEmailDialogOpen}
        onSubmit={email => {
          validateEmail(
            { email },
            {
              onSuccess: response => {
                if (!response.data.attributes.valid) {
                  toast.negative(
                    new Error('This email is already associated with another account.'),
                  )
                  return
                }

                setPendingNewEmail(email)
                createEmailChallenge(undefined, {
                  onSuccess: challengeResponse => {
                    setEmailChallenge({
                      challengeId: challengeResponse.data.id,
                      emailId: challengeResponse.data.attributes.emailId,
                    })
                    setIsUpdateEmailDialogOpen(false)
                    setIsOldEmailDialogOpen(true)
                  },
                  onError: err => {
                    if (err instanceof Error) {
                      toast.negative(err)
                    } else {
                      toast.negative(new Error('Failed to start email verification.'))
                    }
                  },
                })
              },
              onError: err => {
                if (err instanceof Error) {
                  toast.negative(err)
                } else {
                  toast.negative(new Error('Failed to validate email.'))
                }
              },
            },
          )
        }}
      />

      <OldEmailOtpDialog
        key={`old-email-${oldEmailDialogKey}`}
        open={isOldEmailDialogOpen}
        loading={isEmailFlowLoading}
        currentEmail={currentEmail}
        onOpenChange={open => {
          if (!open) {
            setIsConfirmCloseOpen(true)
          }
        }}
        onSubmit={oneTimePass => {
          if (!emailChallenge) return
          submitEmailChallenge(
            {
              challengeId: emailChallenge.challengeId,
              emailId: emailChallenge.emailId,
              oneTimePass,
              newEmail: pendingNewEmail,
            },
            {
              onSuccess: response => {
                setNewEmailChallenge({
                  challengeId: response.data.id,
                  emailId: response.data.attributes.emailId,
                })
                setIsOldEmailDialogOpen(false)
                setIsNewEmailDialogOpen(true)
              },
              onError: err => {
                if (err instanceof Error) {
                  toast.negative(err)
                } else {
                  toast.negative(new Error('Failed to verify current email.'))
                }
              },
            },
          )
        }}
      />

      <NewEmailOtpDialog
        key={`new-email-${newEmailDialogKey}`}
        open={isNewEmailDialogOpen}
        loading={isEmailFlowLoading}
        newEmail={pendingNewEmail}
        onOpenChange={open => {
          if (!open) {
            setIsConfirmCloseOpen(true)
          }
        }}
        onSubmit={oneTimePass => {
          if (!newEmailChallenge) return
          verifyEmailChallenge(
            {
              challengeId: newEmailChallenge.challengeId,
              emailId: newEmailChallenge.emailId,
              oneTimePass,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: ['profile'] })
                toast.positive('Your email has been updated.')
                resetEmailFlow()
              },
              onError: err => {
                if (err instanceof Error) {
                  toast.negative(err)
                } else {
                  toast.negative(new Error('Failed to verify new email.'))
                }
              },
            },
          )
        }}
      />

      <ConfirmCloseEmailDialog
        open={isConfirmCloseOpen}
        onOpenChange={open => {
          if (!open) {
            setIsConfirmCloseOpen(false)
          }
        }}
        onDiscard={() => {
          resetEmailFlow()
          setIsConfirmCloseOpen(false)
        }}
      />
    </NavBars>
  )
}

type ProfilePageFormProps = {
  loading: boolean
  email: string
  onUpdateEmailClick: () => void
}

function ProfilePageForm(props: ProfilePageFormProps) {
  const maxDate = new Date()
  const minDate = new Date(1900, 0, 1)
  const setFieldValues = useSetFieldValues()

  const datePickerValidator = createDatePickerValidator(minDate, maxDate, 'day')

  useEffect(() => {
    setFieldValues({ email: props.email })
  }, [props.email, setFieldValues])

  return (
    <Flex direction="column" gap={6}>
      <Flex align="center" justify="between" pb={4}>
        <Flex gap={4} align="center">
          <AccountCircleOutlineIcon contrast="mid" intent="primary" size="xl" />
          <Heading level={1} variant="headlineL">
            My Profile
          </Heading>
        </Flex>
      </Flex>
      <Card>
        <Flex gap={6} direction="column">
          <Text variant="titleS">Personal Information</Text>
          <Flex gap={6} direction={{ xs: 'column', sm: 'row' }}>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Given Name"
                name="givenName"
                validate={givenName => {
                  if (!givenName) {
                    return 'This field is required.'
                  }

                  return true
                }}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Family Name"
                name="familyName"
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Middle Name (optional)"
                name="middleName"
              />
            </Flex.Item>
          </Flex>
          <Flex gap={6} direction={{ xs: 'column', sm: 'row' }}>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
              <Field
                as={DatePicker}
                label="Date of Birth"
                name="dateOfBirth"
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                validate={datePickerValidator}
              />
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
              <Field
                as={Combobox}
                label="Nationality"
                name="nationality"
                size="md"
                placeholder="Select"
              >
                {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
                  <Combobox.Option key={countryCode} label={countryName} value={countryCode} />
                ))}
              </Field>
            </Flex.Item>
            <Flex.Item basis={{ xs: '100%', sm: 'calc(33% - 12px)' }}>
              <Field as={Select} label="Gender" name="gender" appearance="styled">
                {Object.entries(GENDER_NAMES).map(([genderCode, genderName]) => (
                  <Select.Option key={genderCode} label={genderName} value={genderCode} />
                ))}
              </Field>
            </Flex.Item>
          </Flex>
          <Button intent="primary" type="submit" loading={props.loading}>
            Save
          </Button>
        </Flex>
      </Card>
      <Card>
        <Flex gap={2} direction="column">
          <Text variant="titleS">Account Information</Text>
          <Flex gap={2} align="end">
            <Flex.Item basis={{ xs: '100%' }}>
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Email"
                name="email"
                disabled
              />
            </Flex.Item>
            <Button intent="primary" emphasis="outlined" onClick={props.onUpdateEmailClick}>
              Update email
            </Button>
          </Flex>
        </Flex>
      </Card>
    </Flex>
  )
}
